import React from 'react';
import { Wifi, Plus, Home, ArrowRight } from 'lucide-react';

/**
 * MeshSystemExplanation component
 * Explains how mesh systems work and how our free-to-use router is a mesh system
 */
const MeshSystemExplanation = () => {
  return (
    <div className="max-w-7xl mx-auto">
      {/* Introduction */}
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-white mb-2">Understanding Mesh WiFi Technology</h3>
        <p className="text-gray-300 max-w-3xl mx-auto">
          Our free-to-use router is part of an advanced mesh system that provides seamless, 
          whole-home coverage without dead zones or connection drops.
        </p>
      </div>
      
      {/* What is Mesh */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 hover:border-purple-500/50 transition-colors mb-8">
        <h3 className="text-purple-400 font-semibold text-xl mb-4">What is a Mesh WiFi System?</h3>
        
        <div className="flex flex-col md:flex-row gap-6">
          <div className="md:w-2/3">
            <p className="text-gray-300 mb-4">
              A mesh WiFi system consists of multiple devices that work together to create a single, 
              seamless network throughout your home. Unlike traditional setups with a single router, 
              mesh systems use a main router and additional "nodes" that communicate with each other 
              to extend coverage to every corner of your home.
            </p>
            
            <div className="bg-purple-900/20 p-4 rounded-lg border border-purple-800/30 mb-4">
              <h4 className="text-purple-300 font-medium mb-2">Key Benefits of Mesh WiFi:</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <Wifi className="h-5 w-5 text-purple-400 mr-3 mt-1 flex-shrink-0" />
                  <span><strong>Seamless Coverage:</strong> No dead zones, even in larger homes or homes with unusual layouts</span>
                </li>
                <li className="flex items-start">
                  <Wifi className="h-5 w-5 text-purple-400 mr-3 mt-1 flex-shrink-0" />
                  <span><strong>Single Network Name:</strong> Your devices automatically connect to the strongest signal without manual switching</span>
                </li>
                <li className="flex items-start">
                  <Wifi className="h-5 w-5 text-purple-400 mr-3 mt-1 flex-shrink-0" />
                  <span><strong>Smart Routing:</strong> Data takes the fastest path through your network for optimal performance</span>
                </li>
                <li className="flex items-start">
                  <Wifi className="h-5 w-5 text-purple-400 mr-3 mt-1 flex-shrink-0" />
                  <span><strong>Easy Management:</strong> Control your entire network from a single app or interface</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="md:w-1/3 flex justify-center items-center">
            <div className="relative">
              <div className="w-64 h-64 bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-full flex items-center justify-center">
                <div className="relative">
                  <div className="absolute w-20 h-20 bg-purple-800/40 rounded-full -top-16 -left-24 flex items-center justify-center">
                    <Wifi className="h-8 w-8 text-purple-400" />
                  </div>
                  <div className="absolute w-20 h-20 bg-purple-800/40 rounded-full top-16 -right-24 flex items-center justify-center">
                    <Wifi className="h-8 w-8 text-purple-400" />
                  </div>
                  <div className="absolute w-20 h-20 bg-purple-800/40 rounded-full -bottom-16 -left-8 flex items-center justify-center">
                    <Wifi className="h-8 w-8 text-purple-400" />
                  </div>
                  <div className="w-28 h-28 bg-purple-700/60 rounded-full flex items-center justify-center">
                    <Wifi className="h-12 w-12 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Our Mesh Router */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 hover:border-purple-500/50 transition-colors mb-8">
        <h3 className="text-purple-400 font-semibold text-xl mb-4">Our Free-to-Use Mesh Router</h3>
        
        <div className="flex flex-col md:flex-row gap-6">
          <div className="md:w-1/3 flex justify-center items-center">
            <div className="relative w-64 h-64">
              <img
                src="https://apextech.group/elements-mesh-router-1.png"
                alt="Elements Mesh Router"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          
          <div className="md:w-2/3">
            <p className="text-gray-300 mb-4">
              The router we provide free with your internet package is a high-performance 
              <strong> Wi-Fi CERTIFIED EasyMesh™</strong> device. This means it's designed from the ground up 
              to work as part of a mesh system, giving you the flexibility to expand your network as needed.
            </p>
            
            <div className="bg-blue-900/20 p-4 rounded-lg border border-blue-800/30 mb-4">
              <h4 className="text-blue-300 font-medium mb-2">Expandable Mesh System:</h4>
              <p className="text-gray-300 mb-3">
                Start with your free router, then easily add more mesh nodes to extend coverage as needed:
              </p>
              
              <div className="flex items-center justify-center space-x-4 mb-3">
                <div className="flex flex-col items-center">
                  <div className="bg-purple-700/60 p-3 rounded-full">
                    <Wifi className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-gray-400 text-sm mt-1">Main Router</span>
                </div>
                
                <Plus className="h-5 w-5 text-gray-500" />
                
                <div className="flex flex-col items-center">
                  <div className="bg-purple-600/60 p-3 rounded-full">
                    <Wifi className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-gray-400 text-sm mt-1">Mesh Node</span>
                </div>
                
                <Plus className="h-5 w-5 text-gray-500" />
                
                <div className="flex flex-col items-center">
                  <div className="bg-purple-600/60 p-3 rounded-full">
                    <Wifi className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-gray-400 text-sm mt-1">Mesh Node</span>
                </div>
                
                <ArrowRight className="h-5 w-5 text-gray-500" />
                
                <div className="flex flex-col items-center">
                  <div className="bg-green-600/60 p-3 rounded-full">
                    <Home className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-gray-400 text-sm mt-1">Full Coverage</span>
                </div>
              </div>
              
              <p className="text-gray-300">
                Additional mesh nodes can be purchased separately and will integrate seamlessly with your 
                existing router. Each node you add extends your network's range while maintaining the same 
                network name and full speed.
              </p>
            </div>
            
            <div className="bg-green-900/20 p-4 rounded-lg border border-green-800/30">
              <h4 className="text-green-300 font-medium mb-2">Solving Coverage Issues:</h4>
              <p className="text-gray-300 mb-3">
                If you're experiencing WiFi dead zones or weak signals in certain areas of your home, 
                adding mesh nodes is the perfect solution:
              </p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <Wifi className="h-5 w-5 text-green-400 mr-3 mt-1 flex-shrink-0" />
                  <span><strong>Multi-story homes:</strong> Place nodes on different floors to ensure coverage throughout</span>
                </li>
                <li className="flex items-start">
                  <Wifi className="h-5 w-5 text-green-400 mr-3 mt-1 flex-shrink-0" />
                  <span><strong>Large homes:</strong> Extend coverage to distant rooms and outdoor areas</span>
                </li>
                <li className="flex items-start">
                  <Wifi className="h-5 w-5 text-green-400 mr-3 mt-1 flex-shrink-0" />
                  <span><strong>Homes with thick walls:</strong> Overcome signal-blocking obstacles</span>
                </li>
                <li className="flex items-start">
                  <Wifi className="h-5 w-5 text-green-400 mr-3 mt-1 flex-shrink-0" />
                  <span><strong>Unusual layouts:</strong> Ensure coverage in L-shaped or split-level homes</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      {/* Coverage Forecasting */}
      <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-xl p-6 border border-purple-800/30 text-center">
        <h3 className="text-white font-semibold text-xl mb-4">Forecast Your Home's WiFi Coverage</h3>
        <p className="text-gray-300 mb-6 max-w-3xl mx-auto">
          Not sure if you need additional mesh nodes? Our Wireless Wonder tool can analyze your home's 
          layout and predict coverage based on your specific situation.
        </p>
        <a 
          href="/wireless-wonder" 
          className="inline-flex items-center px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-full transition-colors text-lg"
        >
          Try Wireless Wonder Coverage Forecasting
          <ArrowRight className="ml-2 h-5 w-5" />
        </a>
      </div>
    </div>
  );
};

export default MeshSystemExplanation;